/**
 * =============================================================================
 * EVERY NUMBER THIS FILE RENDERS CAME OFF THE WIRE. NOTHING IN IT DOES
 * ARITHMETIC.
 * =============================================================================
 *
 * This is the unit's non-negotiable, expressed as a module boundary rather than
 * as a promise: the components below take money as `string`, and there is no `+`,
 * no `*`, no `reduce`, and no `Number()` applied to a money value anywhere in the
 * file. The only numeric conversion is inside `formatMoney`
 * (`features/inquiries/format.ts`), which turns ONE already-final value into
 * grouped digits for `Intl` — a presentation step on a number the server decided.
 *
 * WHY A COMPONENT RATHER THAN A CONVENTION. A totals block is exactly the place
 * a well-meaning edit adds "…and the grand total is subtotal − discount + tax,
 * so we can render it before the preview lands". That derivation is the
 * double-counting hazard §9.1 exists to eliminate (`quotes.service.ts` §9.1
 * note: `grand_total` is what `computeDocument` returned, NOT re-derived). By
 * making the totals a component whose props are five strings, there is nowhere
 * for the arithmetic to go: a caller with only four of them has no fifth to
 * invent, and `quotes-money.test.tsx` asserts the rendered figure is the one
 * passed in, character for character.
 *
 * THE MASKING CASE IS FIRST, NOT LAST. `AmountsMaskingInterceptor` replaces every
 * money field with '•••' for any caller without `amounts.view` in the row's
 * country. So every amount here asks `isMaskedAmount` before anything else —
 * `Number('•••')` is `NaN`, and a totals row rendering NaN (or worse, 0) would
 * tell somebody the quote is worth nothing.
 *
 * WHO IS ACTUALLY IN THAT POSITION — corrected, because the first version of this
 * comment named a role that is not. `apps/api/src/seeds/role-matrix.json` gives
 * `quotes.view` to exactly two roles after the Phase 3a six-role remodel —
 * Country Manager and Accounts — and BOTH also hold `amounts.view`. A Developer
 * holds no quote permission at all and never reaches this component. So NO SEEDED
 * ROLE arrives here masked; the remodel narrowed the list from three roles to two
 * without changing that conclusion.
 *
 * The handling stays, and is not defensive programming. `user_permissions`
 * per-user overrides are a first-class part of the model (the admin screens edit
 * them, and this state is exactly how the review reached it), the interceptor is
 * unconditional and runs on every quote route, and the seeded matrix is a
 * DEFAULT that a country admin may change without touching this file. A client
 * that renders '•••' as a number the day somebody grants `quotes.view` on its own
 * is a client that was relying on a seed file to be a type system.
 */

import { formatMoney, isMaskedAmount, NO_VALUE } from '../inquiries/format';
import { cn } from '../../lib/cn';

/**
 * One server-decided amount.
 *
 * `tnum` (tabular figures) so a column of totals does not ripple — the same
 * treatment the inquiry table gives its value column.
 */
export function ServerAmount({
  value,
  currency,
  className,
}: {
  value: string | null | undefined;
  currency: string | null | undefined;
  className?: string;
}): React.JSX.Element {
  const masked = isMaskedAmount(value);
  return (
    <span
      className={cn('tnum', masked && 'text-fg-secondary', className)}
      title={
        masked
          ? 'Hidden — this account cannot view amounts in this country'
          : undefined
      }
    >
      {formatMoney(value, currency)}
    </span>
  );
}

/**
 * A quantity or a percentage — NOT money, and deliberately not routed through
 * `formatMoney`.
 *
 * The server sends `qty` as `'3.00'` and `discount_pct` as `'5.000'`, because a
 * DECIMAL column has a scale and U1 made both routes speak the column's spelling
 * (a preview that echoed the client's `'3'` while the record said `'3.00'` was a
 * LOW finding, fixed). Those trailing zeros are noise to a reader, so this trims
 * them for DISPLAY only — the value that goes back to the server is the state the
 * form holds, never this string.
 */
export function ServerQuantity({
  value,
  suffix,
}: {
  value: string | null | undefined;
  suffix?: string;
}): React.JSX.Element {
  if (value === null || value === undefined || value.trim() === '') {
    return <span className="tnum">{NO_VALUE}</span>;
  }
  if (isMaskedAmount(value)) {
    return <span className="tnum text-fg-secondary">{value}</span>;
  }
  // Purely lexical: strip a trailing '.000' or '.500' → '.5'. No parse, so a value
  // this client does not recognise passes through verbatim rather than becoming
  // NaN.
  const trimmed = /^-?\d+\.\d+$/.test(value.trim())
    ? value.trim().replace(/0+$/, '').replace(/\.$/, '')
    : value.trim();
  return (
    <span className="tnum">
      {trimmed}
      {suffix ?? ''}
    </span>
  );
}

/**
 * A DEDUCTION, rendered as one.
 *
 * A column of amounts is an instruction to add them, and `Discount 100.00`
 * between a subtotal and a tax reads as a contribution unless something says
 * otherwise. On the BUILDER the `Taxable base` row said otherwise; on the two
 * RECORD screens there is no such column on the wire, so reading downward gave
 * 1,000 + 100 + 162 = 1,262 against a printed 1,062 (W8).
 *
 * The sign is a SIBLING TEXT NODE, not part of the amount: `ServerAmount` still
 * renders the server's string untouched, and the minus is a presentation mark
 * this component adds — the same class of act as grouping the digits. It is a
 * real character rather than a decoration, so a screen reader says "minus".
 *
 * A MASKED or ABSENT value is not signed. `−•••` and `−—` are not statements
 * about money, and prefixing them would turn "hidden" into something that looks
 * like a figure.
 */
export function ServerDeduction({
  value,
  currency,
}: {
  value: string | null | undefined;
  currency: string | null | undefined;
}): React.JSX.Element {
  const signable =
    !isMaskedAmount(value) && value !== null && value !== undefined && value.trim() !== '';
  if (!signable) return <ServerAmount value={value} currency={currency} />;
  return (
    <span className="tnum">
      {'−'}
      <ServerAmount value={value} currency={currency} />
    </span>
  );
}

/** One row of the totals block. */
function TotalsRow({
  label,
  detail,
  children,
  strong = false,
  hint,
}: {
  label: string;
  /**
   * A second line under the label, VISIBLE — for the figure a reader needs in
   * order to follow the column and would otherwise have to hover a `title` to
   * find. A tax's base is the case this exists for.
   */
  detail?: string;
  children: React.ReactNode;
  strong?: boolean;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <span className="flex min-w-0 flex-col">
        <span
          className={cn(
            'text-label',
            // `text/secondary`, never `text/muted`: muted is 2.63:1 on a light
            // surface and these are labels for money.
            strong ? 'font-medium text-fg' : 'text-fg-secondary',
          )}
          title={hint}
        >
          {label}
        </span>
        {detail === undefined ? null : (
          <span className="text-label text-fg-secondary">{detail}</span>
        )}
      </span>
      <span className={cn(strong ? 'text-h3 text-fg' : 'text-body text-fg')}>{children}</span>
    </div>
  );
}

export interface DocumentTotals {
  subtotal: string;
  discount_total: string;
  /**
   * §9.1's intermediate — the value `grand_total` was built FROM. Present on a
   * PREVIEW and absent on a RECORD, because the record returns columns and
   * `taxable_base` is not one. Shown when it is there precisely because the
   * builder is the diagnostic surface: a reader can see the pipeline instead of
   * being asked to trust it.
   */
  taxable_base?: string;
  /**
   * The tax that is INSIDE `grand_total`. Required on both the preview and the
   * record, and — until W4 — rendered on neither: the block showed the named
   * `taxes[]` rows instead, and "Not resolved" when there were none. It is now
   * always on screen, because it is the only tax figure the grand total is
   * answerable to.
   */
  tax_total: string;
  grand_total: string;
}

export interface TaxLine {
  tax_name: string;
  rate: string;
  base_amount: string;
  tax_amount: string;
}

/**
 * The D6 tax BREAKDOWN — the named rows, and only those.
 *
 * D6 makes Tally the tax master and this document "indicative": there is no GST
 * split (CGST/SGST/IGST are one "GST" row for India), and the quote's own footer
 * says the invoice governs. If the server ever sends more than one row this
 * renders ALL of them rather than silently showing the first — the U4 review
 * found exactly that class of bug in the PDF template ("a two-tax-row quote was
 * refused a PDF but sent+confirmed fine"), and a UI that hides the second row is
 * the same defect wearing a different hat.
 *
 * THE ZERO CASE IS NOT HANDLED HERE ANY MORE. It used to be, as a row reading
 * "Tax · Not resolved" — and that row REPLACED the only place `totals.tax_total`
 * could have been shown, so a document with an unresolved breakdown and a real
 * tax inside `grand_total` printed "Not resolved" above a figure that contained
 * it (W4). The tax that entered the grand total is now stated unconditionally by
 * {@link TotalsBlock}; the missing breakdown is stated beside it. Both facts, not
 * one instead of the other.
 *
 * EACH ROW SHOWS ITS BASE, ON SCREEN. It was in a `title` attribute, which is a
 * mouse-only affordance and absent on touch. The base is how a reader follows
 * subtotal → discount → tax on a RECORD, where `taxable_base` is not a column
 * the API returns (W8).
 */
export function TaxLines({
  taxes,
  currency,
}: {
  taxes: readonly TaxLine[];
  currency: string | null | undefined;
}): React.JSX.Element {
  return (
    <>
      {taxes.map((tax) => (
        <TotalsRow
          key={`${tax.tax_name}-${tax.rate}`}
          label={`${tax.tax_name} @ ${tax.rate}%`}
          detail={`on ${formatMoney(tax.base_amount, currency)}`}
        >
          <ServerAmount value={tax.tax_amount} currency={currency} />
        </TotalsRow>
      ))}
    </>
  );
}

/**
 * Subtotal → discount → (taxable base) → tax → grand total.
 *
 * Every figure is a prop. This component cannot produce a number that was not
 * handed to it.
 */
export function TotalsBlock({
  totals,
  taxes,
  currency,
  stale = false,
  className,
}: {
  totals: DocumentTotals;
  taxes: readonly TaxLine[];
  currency: string | null | undefined;
  /**
   * The figures belong to an EARLIER basket than the one on screen — the debounce
   * window in the builder. Rendered as reduced opacity plus a word, never as a
   * spinner over the numbers: the old totals are still true about the old basket,
   * and hiding them would leave the operator with nothing to compare against.
   */
  stale?: boolean;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      aria-busy={stale}
      className={cn(
        'flex flex-col gap-2 rounded-md border border-line bg-surface p-5',
        // 150ms, opacity only, inside motion-safe — hard rule 4.
        'motion-safe:transition-opacity motion-safe:duration-settle',
        stale && 'opacity-60',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-th uppercase text-fg-secondary">Totals</h2>
        <span className="text-label text-fg-secondary">
          {stale ? 'Re-pricing…' : 'Priced by the server'}
        </span>
      </div>

      <TotalsRow label="Subtotal">
        <ServerAmount value={totals.subtotal} currency={currency} />
      </TotalsRow>
      <TotalsRow label="Discount">
        {/* SIGNED — see `ServerDeduction`. The column is read downward. */}
        <ServerDeduction value={totals.discount_total} currency={currency} />
      </TotalsRow>
      {totals.taxable_base === undefined ? null : (
        <TotalsRow
          label="Taxable base"
          hint="§9.1's own intermediate — the figure the grand total is built from."
        >
          <ServerAmount value={totals.taxable_base} currency={currency} />
        </TotalsRow>
      )}
      <TaxLines taxes={taxes} currency={currency} />
      {/*
       * `tax_total` — THE FIGURE `grand_total` WAS BUILT FROM, ALWAYS RENDERED.
       *
       * It is required on `DocumentTotals` and used to appear on screen nowhere:
       * the block showed the NAMED rows instead, or "Not resolved" when there
       * were none. Two ways that lied. With no named row, a document carrying a
       * real `tax_total` printed "Not resolved" above a grand total containing
       * it. With two named rows, neither of the amounts shown was the one that
       * entered the grand total (90 and 60, against 150).
       *
       * Stating it unconditionally makes the stated tax and the grand total
       * incapable of disagreeing, at the cost of one repeated line on the
       * single-row documents D6 actually produces — which is how an invoice
       * prints a tax anyway: breakdown, then total.
       */}
      <TotalsRow
        label="Tax"
        detail={taxes.length === 0 ? 'No tax breakdown was resolved' : undefined}
        hint={
          taxes.length === 0
            ? 'The document carries a tax total but no named tax row. That gap is a ' +
              'configuration defect the API gates on (422); the amount is the server’s.'
            : 'The total the grand total was built from.'
        }
      >
        <ServerAmount value={totals.tax_total} currency={currency} />
      </TotalsRow>

      <div className="mt-1 border-t border-line pt-3">
        <TotalsRow label="Grand total" strong>
          <ServerAmount value={totals.grand_total} currency={currency} />
        </TotalsRow>
      </div>

      {/*
       * D6's own sentence, and the reason this whole screen exists in the shape it
       * does: the document is indicative and the invoice governs. It is printed on
       * the PDF (`quote-document.template.ts`) and it belongs on screen for the
       * same reason — the person quoting should read the same disclaimer the
       * customer will.
       */}
      <p className="pt-1 text-label text-fg-secondary">
        Taxes as applicable; invoice values per the selling entity&rsquo;s records govern.
      </p>
    </div>
  );
}
